/* src/core/cities.ts — the travel destinations.
 *
 * Every entry is a real CBD, because the survey system needs somewhere worth pointing the
 * camera: dense enough that the first surveyed cell already looks like a city. The world is
 * not limited to this list — travel is just a viewpoint jump, and surveys run wherever the
 * camera ends up — but a curated list beats typing coordinates.
 */

export interface City {
  readonly name: string;
  readonly state: string;
  readonly lon: number;
  readonly lat: number;
}

/**
 * The playable extent — must match `within_australia()` in migration 0040, the server-side
 * guard this mirrors. The client checks first so cells over the open ocean or another country
 * are never even requested (an unrequestable cell would otherwise be re-asked every poll).
 */
export const AUSTRALIA_BBOX = { west: 112.5, south: -44.2, east: 154.2, north: -9.8 } as const;

export function withinAustralia(lon: number, lat: number): boolean {
  return (
    lon >= AUSTRALIA_BBOX.west &&
    lon <= AUSTRALIA_BBOX.east &&
    lat >= AUSTRALIA_BBOX.south &&
    lat <= AUSTRALIA_BBOX.north
  );
}

export const AU_CITIES: readonly City[] = [
  { name: 'Sydney', state: 'NSW', lon: 151.2093, lat: -33.8688 },
  { name: 'Marrickville (start)', state: 'NSW', lon: 151.104, lat: -33.913 },
  { name: 'Melbourne', state: 'VIC', lon: 144.9631, lat: -37.8136 },
  { name: 'Brisbane', state: 'QLD', lon: 153.026, lat: -27.4705 },
  { name: 'Perth', state: 'WA', lon: 115.8613, lat: -31.9523 },
  { name: 'Adelaide', state: 'SA', lon: 138.6007, lat: -34.9285 },
  { name: 'Canberra', state: 'ACT', lon: 149.13, lat: -35.2809 },
  { name: 'Gold Coast', state: 'QLD', lon: 153.4145, lat: -28.0023 },
  { name: 'Newcastle', state: 'NSW', lon: 151.7817, lat: -32.9283 },
  { name: 'Wollongong', state: 'NSW', lon: 150.8931, lat: -34.4278 },
  { name: 'Hobart', state: 'TAS', lon: 147.3272, lat: -42.8821 },
  { name: 'Geelong', state: 'VIC', lon: 144.3598, lat: -38.1499 },
  { name: 'Sunshine Coast', state: 'QLD', lon: 153.0913, lat: -26.6528 },
  { name: 'Townsville', state: 'QLD', lon: 146.8169, lat: -19.259 },
  { name: 'Cairns', state: 'QLD', lon: 145.771, lat: -16.9186 },
  { name: 'Darwin', state: 'NT', lon: 130.8411, lat: -12.4611 },
  { name: 'Toowoomba', state: 'QLD', lon: 151.9507, lat: -27.5598 },
  { name: 'Ballarat', state: 'VIC', lon: 143.8503, lat: -37.5622 },
  { name: 'Bendigo', state: 'VIC', lon: 144.2802, lat: -36.757 },
  { name: 'Launceston', state: 'TAS', lon: 147.1441, lat: -41.4332 },
  { name: 'Mackay', state: 'QLD', lon: 149.1868, lat: -21.1425 },
  { name: 'Rockhampton', state: 'QLD', lon: 150.5089, lat: -23.3791 },
  { name: 'Bunbury', state: 'WA', lon: 115.6396, lat: -33.3271 },
  { name: 'Bundaberg', state: 'QLD', lon: 152.3489, lat: -24.8661 },
  { name: 'Coffs Harbour', state: 'NSW', lon: 153.1094, lat: -30.2963 },
  { name: 'Wagga Wagga', state: 'NSW', lon: 147.3598, lat: -35.1082 },
  { name: 'Albury', state: 'NSW', lon: 146.9135, lat: -36.0737 },
  { name: 'Alice Springs', state: 'NT', lon: 133.8807, lat: -23.698 },
  { name: 'Broome', state: 'WA', lon: 122.2361, lat: -17.9554 },
];
